import "./_side-drawer.scss";
import React, {useMemo} from 'react';
import {Button} from "../../../components/Button";
import DataEntrySideDrawer from "../../../components/DataEntry/DataEntrySideDrawer";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {
    getEstateFlowchartsSelector,
    selectedFlowchartIdSelector,
    selectIsClientDocumentsOpen,
    setIsClientDocumentsOpen,
    setSideDrawerDetails,
    setSideDrawerOpen,
    sideDrawerDetailsSelector,
    sideDrawerOpenStateSelector
} from "../../slices/EstateFlowChartSlice";
import {DistributionType, DrawerType, FlowchartEstateItemType} from "../../model/EstateFlowchartTypes";
import SideDrawerContentTrust from "./SideDrawerContentTrust";
import {findIrrevocableTrustById, findMiscellaneousById, findTrustInFlowchart} from "../../utils/DefUtils";
import ScrollableContainer from "src/components/ScrollableContainer/ScrollableContainer";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";

export type SideDrawerDetails = {
    parentEstateItemId: string,
    parentEstateItemType: FlowchartEstateItemType,
    id: string | undefined,
    cardType: DrawerType,
    title: string
};

const SideDrawer: React.FC = () => {
    const dispatch = useAppDispatch();
    const isSideDrawerOpen = useAppSelector(sideDrawerOpenStateSelector);
    const estateFlowcharts = useAppSelector(getEstateFlowchartsSelector);
    const flowchartId = useAppSelector(selectedFlowchartIdSelector);
    const sideDrawerDetails = useAppSelector(sideDrawerDetailsSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const isClientDocumentsDrawerOpen = useAppSelector(selectIsClientDocumentsOpen);

    const matchingFlowchart = estateFlowcharts.find(flowchart => flowchart.flowchartId === flowchartId);

    const {cardDetails} = useMemo(() => {
        let cardDetails;

        if (matchingFlowchart && sideDrawerDetails) {
            switch (sideDrawerDetails?.cardType) {
                case FlowchartEstateItemType.REVOCABLE:
                    cardDetails = matchingFlowchart.revocableTrust;
                    break;
                case FlowchartEstateItemType.IRREVOCABLE:
                    cardDetails = findIrrevocableTrustById(sideDrawerDetails?.id, matchingFlowchart);
                    break;
                case FlowchartEstateItemType.MISCELLANEOUS:
                    cardDetails = findMiscellaneousById(sideDrawerDetails?.id, matchingFlowchart);
                    break;
                case DistributionType.CHARITY:
                case DistributionType.OUTRIGHT:
                    cardDetails = findTrustInFlowchart(sideDrawerDetails?.id,
                        sideDrawerDetails.parentEstateItemId,
                        sideDrawerDetails.parentEstateItemType,
                        matchingFlowchart);
                    break;
                case DistributionType.WILL:
                    if (matchingFlowchart.willSummary) {
                        if (sideDrawerDetails.parentEstateItemId === matchingFlowchart.willSummary.primaryWill?.trustId) {
                            cardDetails = matchingFlowchart.willSummary.primaryWill;
                        } else if (sideDrawerDetails.parentEstateItemId === matchingFlowchart.willSummary.partnerWill?.trustId) {
                            cardDetails = matchingFlowchart.willSummary.partnerWill;
                        } else {
                            cardDetails = undefined;
                        }
                    }
                    break;
                case DistributionType.CREDIT_SHELTER:
                case DistributionType.IN_TRUST:
                case DistributionType.MARITAL:
                case DistributionType.SURVIVOR:
                    cardDetails = findTrustInFlowchart(sideDrawerDetails?.id,
                        sideDrawerDetails?.parentEstateItemId,
                        sideDrawerDetails?.parentEstateItemType,
                        matchingFlowchart);
            }
        }
        return {cardDetails};
    }, [sideDrawerDetails, matchingFlowchart]);


    const closeDrawer = () => {
        dispatch(setSideDrawerOpen(false));
        dispatch(setSideDrawerDetails(undefined));
    };

    const handleViewClientDocuments = () => {
        if (!isClientDocumentsDrawerOpen && releaseToggles?.enableEstateFlowchartMVPRelease) {
            dispatch(setIsClientDocumentsOpen(true));
        }
    };

    const Container = releaseToggles?.enableEstateFlowchartMVPRelease
        ? ScrollableContainer
        : 'div';

    return (
        <DataEntrySideDrawer data-testid="data-entry-side-drawer-overlay"
                             isOpen={isSideDrawerOpen}
                             onOverlayClick={closeDrawer}>
            <div className="side-drawer-header">
                <span className="header-title">{sideDrawerDetails?.title}</span>
                <Button className="close-drawer-button" ariaLabel="close" icon="only" iconName="close" size="small"
                        kind="borderless"
                        noPadding onClick={closeDrawer}/>
            </div>

            <Container id="side-drawer-scroll-container" className="side-drawer-content-container">
                {cardDetails && sideDrawerDetails?.cardType &&
                    <SideDrawerContentTrust sideDrawerItem={cardDetails} trustType={sideDrawerDetails.cardType}/>}
            </Container>
            <div className="side-drawer-footer">
                <Button
                    className="view-legal-docs-button"
                    kind="borderless"
                    onClick={handleViewClientDocuments}
                >
                    VIEW LEGAL DOCS
                </Button>
            </div>
        </DataEntrySideDrawer>
    );
};

export default SideDrawer;
